import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { CoachLayout } from "@/components/CoachLayout";
import { ClientLayout } from "@/components/ClientLayout";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Apply from "@/pages/Apply";
import Guide from "@/pages/Guide";
import PlateGuide from "@/pages/PlateGuide";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Welcome from "@/pages/Welcome";
import AuthCallback from "@/pages/AuthCallback";

import CoachOverview from "@/pages/coach/Overview";
import CoachClients from "@/pages/coach/Clients";
import CoachLeads from "@/pages/coach/Leads";
import CoachMTM from "@/pages/coach/MTM";
import CoachMTMWeek from "@/pages/coach/MTMWeek";
import CoachPrograms from "@/pages/coach/Programs";
import CoachProgramBuilder from "@/pages/coach/ProgramBuilder";
import CoachExercises from "@/pages/coach/Exercises";
import CoachMeals from "@/pages/coach/Meals";
import CoachMessages from "@/pages/coach/Messages";
import CoachForums from "@/pages/coach/Forums";
import CoachResources from "@/pages/coach/Resources";
import CoachAIStudio from "@/pages/coach/AIStudio";
import CoachEmails from "@/pages/coach/Emails";

import ClientToday from "@/pages/client/Today";
import ClientWorkouts from "@/pages/client/Workouts";
import ClientMTM from "@/pages/client/MTM";
import ClientMTMWeek from "@/pages/client/MTMWeek";
import ClientNutrition from "@/pages/client/Nutrition";
import ClientHabits from "@/pages/client/Habits";
import ClientProgress from "@/pages/client/Progress";
import ClientMessages from "@/pages/client/Messages";
import ClientForums from "@/pages/client/Forums";
import ClientResources from "@/pages/client/Resources";
import ClientAIChat from "@/pages/client/AIChat";
import ClientWins from "@/pages/client/Wins";
import ClientRecipes from "@/pages/client/Recipes";
import ClientCycle from "@/pages/client/Cycle";
import ClientPlanner from "@/pages/client/Planner";
import CoachRecipes from "@/pages/coach/Recipes";
import CoachWins from "@/pages/coach/Wins";
import Fenyx from "@/pages/Fenyx";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

function AppRouter() {
  const location = useLocation();
  // Detect Emergent OAuth fragment synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/apply" element={<Apply />} />
      <Route path="/guide" element={<Guide />} />
      <Route path="/plate" element={<PlateGuide />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/welcome" element={<Welcome />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Coach */}
      <Route path="/coach" element={<ProtectedRoute role="coach"><CoachLayout><CoachOverview /></CoachLayout></ProtectedRoute>} />
      <Route path="/coach/leads" element={<ProtectedRoute role="coach"><CoachLayout><CoachLeads /></CoachLayout></ProtectedRoute>} />
      <Route path="/coach/mtm" element={<ProtectedRoute role="coach"><CoachLayout><CoachMTM /></CoachLayout></ProtectedRoute>} />
      <Route path="/coach/mtm/weeks/:weekNumber" element={<ProtectedRoute role="coach"><CoachLayout><CoachMTMWeek /></CoachLayout></ProtectedRoute>} />
      <Route path="/coach/clients" element={<ProtectedRoute role="coach"><CoachLayout><CoachClients /></CoachLayout></ProtectedRoute>} />
      <Route path="/coach/programs" element={<ProtectedRoute role="coach"><CoachLayout><CoachPrograms /></CoachLayout></ProtectedRoute>} />
      <Route path="/coach/programs/:programId" element={<ProtectedRoute role="coach"><CoachLayout><CoachProgramBuilder /></CoachLayout></ProtectedRoute>} />
      <Route path="/coach/exercises" element={<ProtectedRoute role="coach"><CoachLayout><CoachExercises /></CoachLayout></ProtectedRoute>} />
      <Route path="/coach/meals" element={<ProtectedRoute role="coach"><CoachLayout><CoachMeals /></CoachLayout></ProtectedRoute>} />
      <Route path="/coach/recipes" element={<ProtectedRoute role="coach"><CoachLayout><CoachRecipes /></CoachLayout></ProtectedRoute>} />
      <Route path="/coach/wins" element={<ProtectedRoute role="coach"><CoachLayout><CoachWins /></CoachLayout></ProtectedRoute>} />
      <Route path="/coach/messages" element={<ProtectedRoute role="coach"><CoachLayout><CoachMessages /></CoachLayout></ProtectedRoute>} />
      <Route path="/coach/forums" element={<ProtectedRoute role="coach"><CoachLayout><CoachForums /></CoachLayout></ProtectedRoute>} />
      <Route path="/coach/resources" element={<ProtectedRoute role="coach"><CoachLayout><CoachResources /></CoachLayout></ProtectedRoute>} />
      <Route path="/coach/ai" element={<ProtectedRoute role="coach"><CoachLayout><CoachAIStudio /></CoachLayout></ProtectedRoute>} />
      <Route path="/coach/emails" element={<ProtectedRoute role="coach"><CoachLayout><CoachEmails /></CoachLayout></ProtectedRoute>} />

      {/* Client */}
      <Route path="/app" element={<ProtectedRoute role="client"><ClientLayout><ClientToday /></ClientLayout></ProtectedRoute>} />
      <Route path="/app/program" element={<ProtectedRoute role="client"><ClientLayout><ClientMTM /></ClientLayout></ProtectedRoute>} />
      <Route path="/app/program/week/:weekNumber" element={<ProtectedRoute role="client"><ClientLayout><ClientMTMWeek /></ClientLayout></ProtectedRoute>} />
      <Route path="/app/workouts" element={<ProtectedRoute role="client"><ClientLayout><ClientWorkouts /></ClientLayout></ProtectedRoute>} />
      <Route path="/app/nutrition" element={<ProtectedRoute role="client"><ClientLayout><ClientNutrition /></ClientLayout></ProtectedRoute>} />
      <Route path="/app/recipes" element={<ProtectedRoute role="client"><ClientLayout><ClientRecipes /></ClientLayout></ProtectedRoute>} />
      <Route path="/app/planner" element={<ProtectedRoute role="client"><ClientLayout><ClientPlanner /></ClientLayout></ProtectedRoute>} />
      <Route path="/fenyx" element={<Fenyx />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/app/cycle" element={<ProtectedRoute role="client"><ClientLayout><ClientCycle /></ClientLayout></ProtectedRoute>} />
      <Route path="/app/habits" element={<ProtectedRoute role="client"><ClientLayout><ClientHabits /></ClientLayout></ProtectedRoute>} />
      <Route path="/app/progress" element={<ProtectedRoute role="client"><ClientLayout><ClientProgress /></ClientLayout></ProtectedRoute>} />
      <Route path="/app/messages" element={<ProtectedRoute role="client"><ClientLayout><ClientMessages /></ClientLayout></ProtectedRoute>} />
      <Route path="/app/forums" element={<ProtectedRoute role="client"><ClientLayout><ClientForums /></ClientLayout></ProtectedRoute>} />
      <Route path="/app/resources" element={<ProtectedRoute role="client"><ClientLayout><ClientResources /></ClientLayout></ProtectedRoute>} />
      <Route path="/app/ai" element={<ProtectedRoute role="client"><ClientLayout><ClientAIChat /></ClientLayout></ProtectedRoute>} />
      <Route path="/app/wins" element={<ProtectedRoute role="client"><ClientLayout><ClientWins /></ClientLayout></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster theme="light" position="top-right" toastOptions={{ style: { fontFamily: "Montserrat, sans-serif", borderRadius: 0 } }} />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
